import React, { useEffect, useState } from "react";
import { Tooltip } from "flowbite-react";
import GenericApi from "@/api/genericApi";
import {
  CoPQueryKey,
  GenericStatusQueryKey,
  QuestionaryQueryKey,
} from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiError, ApiResponse } from "@/types/Api";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormError, ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import {
  checkPermissionByKeyAndValue,
  getStatementOfPurposeRequestData,
  isBitramp,
  mapSopToBoolean,
} from "@/utils/utils";
import { AxiosError, isAxiosError } from "axios";
import { formatError } from "@/utils/formatError.tsx";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/store";
import { Status } from "@/types/Status.tsx";
import { setActiveTab } from "@/store/slices/componentSlice";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import { FinancialInfoNEW, StatusForms } from "@/types/BusinessInfoNEW";
import { getUser } from "@/utils/localStorage";
import { Button } from "../UI/Button";

const QuestionaryForm: React.FC = () => {
  const user = getUser();
  const { selectedClient } = useSelector((state: RootState) => state.client);

  const SoPData = ["Earnings", "Investments", "Purchase_of_Property", "Loans"];

  const { showSnackBar } = useSnackBarAlert();

  const { data: userFinancialInfo } = useQuery<ApiResponse<FinancialInfoNEW>>({
    queryFn: async () =>
      GenericApi.get<FinancialInfoNEW>(
        `${
          selectedClient
            ? `${ApiRoutes.QUESTIONARYINFO}?user_id=${selectedClient.id}`
            : `${ApiRoutes.QUESTIONARYINFO}`
        }`,
      ),
    queryKey: [QuestionaryQueryKey],
  });

  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  const sopData = mapSopToBoolean(
    userFinancialInfo?.data?.statement_of_purpose ?? null,
    SoPData,
  );

  const [declarations, setDeclarations] = useState({
    pepDeclaration: false,
    liability: false,
    informationTrue: false,
    trueInfo: true,
  });

  const [declineReason, setDeclineReason] = useState<string>(
    userFinancialInfo?.data?.status?.decline_reason ?? "",
  );

  const [formData, setFormData] = useState<FinancialInfoNEW>({
    politically_exposed: userFinancialInfo?.data?.politically_exposed ?? false,
    politically_exposed_details:
      userFinancialInfo?.data?.politically_exposed_details ?? "",
    company_received: userFinancialInfo?.data?.company_received ?? true,
    company_party: userFinancialInfo?.data?.company_party ?? true,
    company_services: userFinancialInfo?.data?.company_services ?? true,
    company_employee: userFinancialInfo?.data?.company_employee ?? true,
    company_mica: userFinancialInfo?.data?.company_mica ?? true,
    bankruptcy: userFinancialInfo?.data?.bankruptcy ?? false,
    litigation: userFinancialInfo?.data?.litigation ?? false,
    company_marketing: userFinancialInfo?.data?.company_marketing ?? true,
    criminal_offenses: userFinancialInfo?.data?.criminal_offenses ?? false,
    criminal_offenses_details:
      userFinancialInfo?.data?.criminal_offenses_details ?? "",
    source_of_income: userFinancialInfo?.data?.source_of_income ?? "",
    source_of_wealth: userFinancialInfo?.data?.source_of_wealth ?? "",
    source_of_funds: userFinancialInfo?.data?.source_of_funds ?? "",
    date: userFinancialInfo?.data?.date ?? "",
    statement_of_purpose: userFinancialInfo?.data?.statement_of_purpose ?? [],
    otherPurpose:
      userFinancialInfo?.data?.statement_of_purpose?.filter(
        (st) => !SoPData.includes(st),
      )?.[0] ?? "",
    Earnings: sopData?.["Earnings"] ?? false,
    Investments: sopData?.["Investments"] ?? false,
    Purchase_of_Property: sopData?.["Purchase_of_Property"] ?? false,
    Loans: sopData?.["Loans"] ?? false,
    status: {
      status: userFinancialInfo?.data?.status?.status ?? "",
      decline_reason: userFinancialInfo?.data?.status?.decline_reason ?? "",
    },
  });

  useEffect(() => {
    if (userFinancialInfo?.data) {
      setFormData({
        politically_exposed:
          userFinancialInfo?.data?.politically_exposed ?? false,
        politically_exposed_details:
          userFinancialInfo?.data?.politically_exposed_details ?? "",
        company_marketing: userFinancialInfo?.data?.company_marketing ?? "",
        company_received: userFinancialInfo?.data?.company_received ?? "",
        company_party: userFinancialInfo?.data?.company_party ?? "",
        company_services: userFinancialInfo?.data?.company_services ?? "",
        company_employee: userFinancialInfo?.data?.company_employee ?? "",
        company_mica: userFinancialInfo?.data?.company_mica ?? "",
        bankruptcy: userFinancialInfo?.data?.bankruptcy ?? false,
        litigation: userFinancialInfo?.data?.litigation ?? false,
        criminal_offenses: userFinancialInfo?.data?.criminal_offenses ?? false,
        criminal_offenses_details:
          userFinancialInfo?.data?.criminal_offenses_details ?? "",
        source_of_income: userFinancialInfo?.data?.source_of_income ?? "",
        source_of_wealth: userFinancialInfo?.data?.source_of_wealth ?? "",
        source_of_funds: userFinancialInfo?.data?.source_of_funds ?? "",
        date:
          userFinancialInfo?.data?.date ??
          new Date().toISOString().split("T")[0],
        statement_of_purpose:
          userFinancialInfo?.data?.statement_of_purpose ?? [],
        otherPurpose:
          userFinancialInfo?.data?.statement_of_purpose?.filter(
            (st) => !SoPData.includes(st),
          )?.[0] ?? "",
        status: {
          status: userFinancialInfo?.data?.status?.status ?? "",
          decline_reason: userFinancialInfo?.data?.status?.decline_reason ?? "",
        },
        Earnings: sopData?.["Earnings"] ?? false,
        Investments: sopData?.["Investments"] ?? false,
        Purchase_of_Property: sopData?.["Purchase_of_Property"] ?? false,
        Loans: sopData?.["Loans"] ?? false,
      });
      setDeclineReason(userFinancialInfo?.data?.status?.decline_reason ?? "");
    }
  }, [userFinancialInfo, userFinancialInfo]);

  const [error, setError] = useState<{
    type: "manual";
    message: string;
  }>({
    type: "manual",
    message: "",
  });

  const canEdit = user?.is_superuser;

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    const defaultErrorMessage = "An error occurred";

    if (
      isAxiosError<ValidationErrors>(error) &&
      Array.isArray(error.response?.data.detail)
    ) {
      setError({
        type: "manual",
        message: formatError(error, defaultErrorMessage),
      });
      return;
    }

    if (isAxiosError<FormError>(error)) {
      setError({
        type: "manual",
        message: error.response?.data?.detail ?? defaultErrorMessage,
      });
      return;
    }

    //* Set error message from server
    setError({
      type: "manual",
      message: error?.message ?? defaultErrorMessage,
    });
  };

  const localUser = getUser();

  const addQuestionary = useMutation<
    ApiResponse<FinancialInfoNEW>,
    ApiError<ValidationErrors>,
    FinancialInfoNEW
  >({
    mutationFn: (data) =>
      GenericApi.put<FinancialInfoNEW>(`${ApiRoutes.QUESTIONARYINFO}`, {
        ...data,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [QuestionaryQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      showSnackBar(
        "Data Saved",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
      dispatch(setActiveTab(3));
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
      handleAxiosError(error);
    },
  });

  const editQuestionaryMutation = useMutation<
    ApiResponse<FinancialInfoNEW>,
    ApiError<ValidationErrors>,
    FinancialInfoNEW
  >({
    mutationFn: (data) =>
      GenericApi.put<FinancialInfoNEW>(
        `${ApiRoutes.QUESTIONARYINFO}?user_id=${
          selectedClient ? selectedClient.id : ""
        }`,
        {
          ...data,
        },
      ),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [QuestionaryQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      showSnackBar(
        "Data Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        "An error occurred",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
      handleAxiosError(error);
    },
  });

  const addQuestionaryStatus = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    StatusForms
  >({
    mutationFn: (data) => {
      return GenericApi.put<StatusForms>(
        `${ApiRoutes.QUESTIONARYINFOSTATUS}?user_id=${selectedClient?.id}`,
        {
          ...data,
        },
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [QuestionaryQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      showSnackBar(
        "Business Information Status Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData({
      ...formData,
      [name]:
        type === "radio"
          ? value === "true"
          : type === "checkbox"
            ? checked
            : value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !declarations.pepDeclaration ||
      !declarations.liability ||
      !declarations.informationTrue ||
      !declarations.trueInfo
    ) {
      showSnackBar(
        "You must accept the 4 declarations before submitting.",
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
      return;
    }

    const payload = {
      ...formData,
      statement_of_purpose: getStatementOfPurposeRequestData(
        sopData,
        formData,
        SoPData,
      ),
    };
    setError({
      type: "manual",
      message: "",
    });
    addQuestionary.mutate(payload);
  };

  useEffect(() => {
    if (selectedClient) {
      const statusFromBackend = userFinancialInfo?.data?.status;
      if (statusFromBackend) {
        setFormData((prev: any) => ({
          ...prev,
          status: statusFromBackend,
        }));
      }
    }
  }, [selectedClient, userFinancialInfo]);

  const handleSubmitWithStatus = (
    e: React.FormEvent,
    statusChanged: string,
  ) => {
    e.preventDefault();

    if (selectedClient) {
      const payload = {
        status: statusChanged,
        decline_reason: declineReason,
      };
      setFormData((prev: any) => ({
        ...prev,
        status: { ...prev.status, status: statusChanged },
      }));
      setError({
        type: "manual",
        message: "",
      });
      addQuestionaryStatus.mutate(payload, {
        onSuccess: () => {
          setFormData((prev: any) => ({
            ...prev,
            status: { ...prev.status, status: statusChanged },
          }));
        },
      });
    }
  };

  const canEditQuestionary =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "financialinfo",
    ) || user?.is_superuser;

  const isDisabled = !(canEditQuestionary || !user?.is_staff);

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full mx-auto Container pshadow p-8 Text"
    >
      <div className="w-full flex items-center pt-2 pb-2 mb-4">
        <h2
          className={`w-fit whitespace-nowrap semi-bold ${
            isBitramp() ? "text-white" : ""
          }`}
        >
          Questionary
        </h2>
        {isBitramp() ? (
          <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
        ) : (
          <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
        )}
      </div>
      {/* Politically Exposed Person Question */}
      <div className="flex flex-col flex-wrap gap-4 mb-6 w-2/3 ml-14">
        <label className="flex w-2/5 SmallText light-blue medium">
          Are you a politically exposed person?
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none !top-80"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none !top-80"
            } `}
            content="**‘Politically exposed person’ means a natural person who is or who has been entrusted with prominent public functions and includes the following: (a) heads of state, heads of government, ministers and deputy or assistant ministers; (b) members of parliament or of similar legislative bodies; (c) members of the governing bodies of political parties; (d) members of supreme courts, of constitutional courts or of other high-level judicial bodies, the decisions of which are not subject to further appeal, except in exceptional circumstances; (e) members of courts of auditors or of the boards of central banks; (f) ambassadors and high-ranking officers in the armed forces; (g) members of the administrative, management or supervisory bodies of state-owned enterprises; (h) directors, deputy directors and members of the board or equivalent function of an international organisation. No public function referred to in points (a) to (h) shall be understood as covering middle-ranking or more junior officials;You also meet the definition of a PEP if you are a close associate or family member of a PEP. Family members include a PEP’s parents, partner (or equivalent), their children and their partners (or equivalent), while a close associate includes a natural person with whom the PEP shares a common business interest or beneficial ownership of a legal entity, legal arrangement or similar or a natural person who has sole beneficial ownership of a legal entity, legal arrangement or similar that is known to have been set up for the benefit of a PEP."
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <div
          className="flex items-center gap-12 mt-[0.3rem]"
          role="radiogroup"
          aria-label="Politically Exposed"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.politically_exposed === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "politically_exposed",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "politically_exposed",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={`${isBitramp() ? "#8AFEFD" : "#202a43"}`}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>

        {formData.politically_exposed && (
          <div className="mt-6 w-full">
            <label
              className={`${
                isBitramp() ? "text-[#9f9f9f]" : "text-[#737791]"
              } block w-full text-sm font-bold`}
            >
              IF YES <br />
              <span className="font-semibold">
                Please provide further details and include your answers on a
                separate sheet if additional space is needed
              </span>
            </label>
            <textarea
              name="politically_exposed_details"
              value={formData.politically_exposed_details}
              onChange={handleChange}
              disabled={isDisabled}
              placeholder="Please provide further details"
              className={`${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } mt-2 w-full border border-gray-300 p-2 rounded-[0.625rem] SmallText Input`}
            />
          </div>
        )}
      </div>

      {isBitramp() ? (
        <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
      ) : (
        <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
      )}

      <div className="flex flex-col flex-wrap gap-4 mb-6 w-2/3 ml-14 mt-4">
        <label className="flex w-2/5 SmallText light-blue medium">
          Bankruptcy / disqualification
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="Have you or any corporation of which you have been an officer, director or a ten percent or more shareholder ever been made bankrupt, or have you ever been disqualified from acting as a company director or officer?"
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <div
          className="flex items-center gap-12 mt-[0.3rem]"
          role="radiogroup"
          aria-label="Bankruptcy"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.bankruptcy === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "bankruptcy",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "bankruptcy",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col flex-wrap gap-4 mb-6 w-2/3 ml-14">
        <label className="flex w-2/5 SmallText light-blue medium">
          Litigation
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="Are there, to your knowledge, any legal proceedings / litigation filed, pending or threatened to be filed against you?"
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <div
          className="flex items-center gap-12 mt-[0.3rem]"
          role="radiogroup"
          aria-label="Litigation"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.litigation === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "litigation",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "litigation",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col flex-wrap gap-4 mb-6 w-2/3 ml-14">
        <label className="flex w-2/5 SmallText light-blue medium">
          Criminal and/or other offences
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="Have you, or any corporation of which you have been an officer, director or ten percent or more shareholder ever been convicted of any crime (other than minor traffic offences), indicted, subject to investigation by a law enforcement agency, or been investigated / sanctioned by a regulatory, supervisory or professional body?"
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <div
          className="flex items-center gap-12 mt-[0.3rem]"
          role="radiogroup"
          aria-label="Criminal Offenses"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.criminal_offenses === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "criminal_offenses",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "criminal_offenses",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap mb-6 w-2/3 ml-14">
        <label className="w-full mt-6 SmallText light-blue medium">
          If you have answered “yes” to any of questions 1-3, please provide
          details.
        </label>
        <textarea
          name="criminal_offenses_details"
          value={formData.criminal_offenses_details}
          onChange={handleChange}
          disabled={isDisabled}
          placeholder="Please provide further details"
          className={`${
            isBitramp() ? "placeholder:text-[#464646]" : ""
          } mt-2 w-full border border-gray-300 p-2 rounded-[0.625rem] SmallText Input`}
        />
      </div>

      {/* Sources and Other Inputs */}

      <div className="w-full ml-auto h-[1px] mb-6 bg-[#0000001a] rounded-lg" />

      <div className="flex flex-wrap mb-6 w-2/3 ml-14">
        <label className="flex SmallText light-blue medium">
          Source of income
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="Please provide details of your current principal source of income (if any), and include relevant details that can be verified (I.E. Employment, business activity, freelancer contract, retirement or disability pensions, …)"
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <textarea
          name="source_of_income"
          value={formData.source_of_income}
          onChange={handleChange}
          disabled={isDisabled}
          placeholder="Please provide further details"
          className={`${
            isBitramp() ? "placeholder:text-[#464646]" : ""
          } mt-2 w-full border border-gray-300 p-2 rounded-[0.625rem] SmallText Input`}
        />
      </div>
      <div className="flex flex-wrap mb-6 w-2/3 ml-14">
        <label className="flex SmallText light-blue medium">
          Source of wealth
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="Please provide us with an explanation as to how you have acquired all your wealth, and include relevant details that can be verified. (I.E. Inheritance, donation, sale of property, sale of shares, business activity,…)"
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <textarea
          name="source_of_wealth"
          value={formData.source_of_wealth}
          onChange={handleChange}
          disabled={isDisabled}
          placeholder="Please provide further details"
          className={`${
            isBitramp() ? "placeholder:text-[#464646]" : ""
          } mt-2 w-full border border-gray-300 p-2 rounded-[0.625rem] SmallText Input`}
        />
      </div>
      <div className="flex flex-wrap mb-6 w-2/3 ml-14">
        <label className="flex SmallText light-blue medium">
          Source of funds
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="Please provide details of the source of funds that you will be using for the transaction. (I.E. Savings, loans, donation, …). Where applicable please also include details of the account that the funds will be remitted from (bank account number, crypto wallet address)."
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <textarea
          name="source_of_funds"
          value={formData.source_of_funds}
          onChange={handleChange}
          disabled={isDisabled}
          placeholder="Please provide further details"
          className={`${
            isBitramp() ? "placeholder:text-[#464646]" : ""
          } mt-2 w-full border border-gray-300 p-2 rounded-[0.625rem] SmallText Input`}
        />
      </div>

      {/* Statement of Purpose */}
      <div className="w-full ml-auto h-[1px] mb-6 bg-[#0000001a] rounded-lg" />

      <div className="flex flex-wrap mb-6 w-2/3 ml-14">
        <label className="flex text-sm font-medium mb-8 text-[#737791] w-full SmallText light-blue">
          Statement of Purpose
          <Tooltip
            className={`${
              isBitramp()
                ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
            } `}
            content="I declare that I am carrying out the transaction for the following purpose"
            placement="right"
            trigger="hover"
          >
            <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
              ?
            </div>
          </Tooltip>
        </label>
        <div className="mt-2 flex flex-col gap-4 ml-8 space-y-2 w-full">
          {Object.entries(sopData).map(([key]) => {
            const isChecked = !!formData[key as keyof typeof formData];
            const formattedKey = key
              .split("_")
              .map((word) => {
                const lower = word.toLowerCase();
                return lower === "of"
                  ? "of"
                  : lower.charAt(0).toUpperCase() + lower.slice(1);
              })
              .join(" ");

            return (
              <div key={key}>
                <div
                  role="checkbox"
                  tabIndex={0}
                  aria-checked={isChecked}
                  onClick={() =>
                    !isDisabled &&
                    handleChange({
                      target: {
                        name: key,
                        checked: !isChecked,
                        type: "checkbox",
                      },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                      e.preventDefault();
                      handleChange({
                        target: {
                          name: key,
                          checked: !isChecked,
                          type: "checkbox",
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }
                  }}
                  className={`inline-flex items-center cursor-pointer space-x-2 outline-none rounded-[0.4rem] px-1 SmallText ${
                    isDisabled ? "text-gray-400 cursor-not-allowed" : ""
                  }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <circle
                      cx="16"
                      cy="16"
                      r="15.5"
                      stroke="#464646"
                      strokeWidth="1.5"
                    />
                    {isChecked && (
                      <circle
                        cx="16"
                        cy="16"
                        r="9"
                        fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                      />
                    )}
                  </svg>
                  <span className="w-48 SmallText light-blue medium">
                    {formattedKey}
                  </span>
                </div>
              </div>
            );
          })}

          <label className="flex items-center">
            <span className="ml-2 w-12 block SmallText light-blue medium">
              Other
            </span>
            <input
              type="text"
              name="otherPurpose"
              value={formData.otherPurpose}
              onChange={handleChange}
              disabled={isDisabled}
              placeholder="Please specify"
              className={`${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } border border-gray-300 p-1 Input`}
            />
          </label>
        </div>
      </div>

      <div className="w-full flex items-center pt-2 pb-2 mb-6">
        <h2
          className={`w-fit whitespace-nowrap semi-bold ${
            isBitramp() ? "text-white" : ""
          }`}
        >
          Declaration
        </h2>
        {isBitramp() ? (
          <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
        ) : (
          <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
        )}
      </div>

      <label className="px-24 inline-flex items-center mb-6">
        <div
          role="checkbox"
          tabIndex={0}
          aria-checked={selectedClient ? true : declarations.trueInfo}
          onClick={() =>
            !isDisabled &&
            setDeclarations({
              ...declarations,
              trueInfo: !declarations.trueInfo,
            })
          }
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
              e.preventDefault();
              setDeclarations({
                ...declarations,
                trueInfo: !declarations.trueInfo,
              });
            }
          }}
          className={`inline-flex items-center space-x-2 cursor-pointer rounded-[0.4rem] px-1 SmallText outline-none ${
            isDisabled ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 32 32"
            fill="none"
          >
            <circle
              cx="16"
              cy="16"
              r="15.5"
              stroke="#464646"
              strokeWidth="1.5"
            />
            {(selectedClient ? true : declarations.trueInfo) && (
              <circle
                cx="16"
                cy="16"
                r="9"
                fill={isBitramp() ? "#8AFEFD" : "#202a43"}
              />
            )}
          </svg>
        </div>

        <span className="ml-8 SmallText light-blue medium">
          I confirm that the information provided by me in this questionnaire is
          true and accurate to the best of my knowledge. I acknowledge that the
          personal information I have provided will be used as part of Leotus's
          compliance and client onboarding procedures, which include background
          checks and verifications, and that the information will be treated in
          accordance with the company's privacy policy.
        </span>
      </label>

      <div className="gap-4 mb-6 w-2/3 ml-[6.5rem]">
        <label className="flex SmallText light-blue medium">
          Your company confirms that it contacted Leotus Limited solely on its
          own initiative, and not as a result of any marketing, solicitation or
          promotion conducted by Leotus Limited or any third party. (Yes/No)
        </label>
        <div
          className="flex items-center gap-12 mt-[0.5rem]"
          role="radiogroup"
          aria-label="Company marketing"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.company_marketing === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "company_marketing",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "company_marketing",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="gap-4 mb-6 w-2/3 ml-[6.5rem]">
        <label className="flex SmallText light-blue medium">
          Your company confirms that it has not received any investment advice
          or recommendations from Leotus Limited or from any individual
          associated with Leotus Limited in connection with its services.
          (Yes/No)
        </label>
        <div
          className="flex items-center gap-12 mt-[0.5rem]"
          role="radiogroup"
          aria-label="Company marketing"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.company_received === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "company_received",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "company_received",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="gap-4 mb-6 w-2/3 ml-[6.5rem]">
        <label className="flex SmallText light-blue medium">
          Your company confirms that it has not been referred, introduced or
          recommended to Leotus Limited by any third party acting on behalf of
          Leotus Limited. (Yes/No)
        </label>
        <div
          className="flex items-center gap-12 mt-[0.5rem]"
          role="radiogroup"
          aria-label="Company marketing"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.company_party === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "company_party",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "company_party",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="gap-4 mb-6 w-2/3 ml-[6.5rem]">
        <label className="flex SmallText light-blue medium">
          Your company confirms that the information received from Leotus
          Limited is for general assessment purposes only and does not
          constitute an offer or solicitation of services.(Yes/No)
        </label>
        <div
          className="flex items-center gap-12 mt-[0.5rem]"
          role="radiogroup"
          aria-label="Company marketing"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.company_services === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "company_services",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "company_services",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="gap-4 mb-6 w-2/3 ml-[6.5rem]">
        <label className="flex SmallText light-blue medium">
          Your company confirms that all information received from Leotus
          Limited will remain confidential and will not be disclosed to any
          third party (other than your company’s directors and
          employees).(Yes/No)
        </label>
        <div
          className="flex items-center gap-12 mt-[0.5rem]"
          role="radiogroup"
          aria-label="Company marketing"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.company_employee === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "company_employee",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "company_employee",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="gap-4 mb-6 w-2/3 ml-[6.5rem]">
        <label className="flex SmallText light-blue medium">
          Your company confirms that Leotus Limited has not offered and does not
          offer financial services within the territory of the European Union
          without the required authorisation in accordance with Article 61 of
          MiCA.(Yes/No)
        </label>
        <div
          className="flex items-center gap-12 mt-[0.5rem]"
          role="radiogroup"
          aria-label="Company marketing"
        >
          {["YES", "NO"].map((value) => {
            const optionValue = value === "YES";
            const isChecked = formData.company_mica === optionValue;

            return (
              <div
                key={value}
                role="radio"
                aria-checked={isChecked}
                tabIndex={0}
                onClick={() =>
                  !isDisabled &&
                  handleChange({
                    target: {
                      name: "company_mica",
                      value: String(optionValue),
                      checked: optionValue,
                      type: "radio",
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                    e.preventDefault();
                    handleChange({
                      target: {
                        name: "company_mica",
                        value: String(optionValue),
                        checked: optionValue,
                        type: "radio",
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                  isChecked ? "font-semibold" : ""
                } ${isDisabled ? "text-gray-400 cursor-not-allowed" : ""}`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <circle
                    cx="16"
                    cy="16"
                    r="15.5"
                    stroke="#464646"
                    strokeWidth="1.5"
                  />
                  {isChecked && (
                    <circle
                      cx="16"
                      cy="16"
                      r="9"
                      fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                    />
                  )}
                </svg>
                <span className="light-blue font-medium">{value}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Declaration Section */}

      <div className="w-full flex items-center pt-2 pb-2 mb-6">
        <h2
          className={`w-fit whitespace-nowrap semi-bold ${
            isBitramp() ? "text-white" : ""
          }`}
        >
          Political Exposure Statement (PEP)
        </h2>
        {isBitramp() ? (
          <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
        ) : (
          <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
        )}
      </div>

      <div className="flex flex-col gap-4 mb-6 px-24">
        <label className="inline-flex items-center">
          <div
            role="checkbox"
            tabIndex={0}
            aria-checked={selectedClient ? true : declarations.pepDeclaration}
            onClick={() =>
              !isDisabled &&
              setDeclarations({
                ...declarations,
                pepDeclaration: !declarations.pepDeclaration,
              })
            }
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                e.preventDefault();
                setDeclarations({
                  ...declarations,
                  pepDeclaration: !declarations.pepDeclaration,
                });
              }
            }}
            className={`inline-flex items-center space-x-2 cursor-pointer rounded-[0.4rem] px-1 SmallText outline-none ${
              isDisabled ? "text-gray-400 cursor-not-allowed" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 32 32"
              fill="none"
            >
              <circle
                cx="16"
                cy="16"
                r="15.5"
                stroke="#464646"
                strokeWidth="1.5"
              />
              {(selectedClient ? true : declarations.pepDeclaration) && (
                <circle
                  cx="16"
                  cy="16"
                  r="9"
                  fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                />
              )}
            </svg>
          </div>

          <span className="ml-8 SmallText light-blue medium">
            I declare that I am not a politically exposed person, a family
            member of a politically exposed person nor a person known to be a
            close associate of a politically exposed person within the meaning
            of the Fourth Anti-Money Laundering Directive as amended by the
            Fifth Anti-Money Laundering Directive (the “Directive”) and the
            Proceeds of Crime Act 2015 in Gibraltar (the “Act”).
          </span>
        </label>
        <label className="inline-flex items-center">
          <div
            role="checkbox"
            tabIndex={0}
            aria-checked={selectedClient ? true : declarations.liability}
            onClick={() =>
              !isDisabled &&
              setDeclarations({
                ...declarations,
                liability: !declarations.liability,
              })
            }
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                e.preventDefault();
                setDeclarations({
                  ...declarations,
                  liability: !declarations.liability,
                });
              }
            }}
            className={`inline-flex items-center space-x-2 cursor-pointer rounded-[0.4rem] px-1 SmallText outline-none ${
              isDisabled ? "text-gray-400 cursor-not-allowed" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 32 32"
              fill="none"
            >
              <circle
                cx="16"
                cy="16"
                r="15.5"
                stroke="#464646"
                strokeWidth="1.5"
              />
              {(selectedClient ? true : declarations.liability) && (
                <circle
                  cx="16"
                  cy="16"
                  r="9"
                  fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                />
              )}
            </svg>
          </div>

          <span className="ml-8 SmallText light-blue medium">
            I am aware of the criminal liability for submitting a false
            declaration. In the event of a change in the above status, I
            undertake to inform Leotus about this fact.
          </span>
        </label>
        <label className="inline-flex items-center">
          <div
            role="checkbox"
            tabIndex={0}
            aria-checked={selectedClient ? true : declarations.informationTrue}
            onClick={() => {
              if (!isDisabled) {
                setDeclarations((prev) => ({
                  ...prev,
                  informationTrue: !prev.informationTrue,
                }));
              }
            }}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !isDisabled) {
                e.preventDefault();
                setDeclarations((prev) => ({
                  ...prev,
                  informationTrue: !prev.informationTrue,
                }));
              }
            }}
            className={`inline-flex items-center space-x-2 cursor-pointer rounded-[0.4rem] px-1 SmallText outline-none ${
              isDisabled ? "text-gray-400 cursor-not-allowed" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 32 32"
              fill="none"
            >
              <circle
                cx="16"
                cy="16"
                r="15.5"
                stroke="#464646"
                strokeWidth="1.5"
              />
              {(selectedClient ? true : declarations.informationTrue) && (
                <circle
                  cx="16"
                  cy="16"
                  r="9"
                  fill={isBitramp() ? "#8AFEFD" : "#202a43"}
                />
              )}
            </svg>
          </div>

          <span className="ml-8 SmallText light-blue medium">
            I hereby declare that the information provided is true and correct.
          </span>
        </label>
      </div>

      {/* Date Field */}
      <div className="my-12 ml-20 block SmallText light-blue medium w-80">
        <label className="">Date</label>
        {/* <input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          min={new Date().toISOString().split("T")[0]}
          max={new Date().toISOString().split("T")[0]}
          disabled={isDisabled}
          className="mt-2 w-full border border-gray-300 p-2 rounded-md Input no-bold-date"
        /> */}
        <input
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          className="mt-2 w-full border border-gray-300 p-2 rounded-md Input no-bold-date input_design"
          disabled={isDisabled}
          min={new Date().toISOString().split("T")[0]}
          max={new Date().toISOString().split("T")[0]}
        />
        <style>
          {`
            input[type="date"].input_design::-webkit-calendar-picker-indicator {
            filter: ${isBitramp() ? "invert(1)" : "invert(0)"};
            cursor: pointer;
            }
           `}
        </style>
      </div>

      {userFinancialInfo?.data?.status?.status == "declined" ||
      user?.is_staff ? (
        <div>
          <div className="w-full flex items-center pt-2 pb-2">
            <h2
              className={`${
                isBitramp() ? "text-white" : ""
              } w-fit whitespace-nowrap font-semibold`}
            >
              Reason of decline
            </h2>
            <div
              className={`${
                isBitramp() ? "bg-[#464646]" : "bg-[#0000001a]"
              } w-full ms-4 ml-auto h-[1px]  rounded-lg`}
            />
          </div>
          <div className="w-[81.5%] mb-6 ml-14">
            <textarea
              id="decline_reason"
              name="decline_reason"
              placeholder="Type"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className={`${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } mt-1 block w-full border-gray-300 rounded-md shadow-s sm:text-sm Input`}
              disabled={!canEditQuestionary}
            />
          </div>
        </div>
      ) : null}

      {/* Submit Button */}
      <div className="flex gap-4">
        {canEditQuestionary ? (
          <>
            <button
              type="button"
              onClick={(e) => handleSubmitWithStatus(e, Status.APPROVED)}
              className="Approve_button"
              disabled={isDisabled}
            >
              {formData?.status?.status === Status.APPROVED
                ? "Approved"
                : "Approve"}
              {formData?.status?.status === Status.APPROVED ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : null}
            </button>
            <button
              type="button"
              onClick={(e) => handleSubmitWithStatus(e, Status.REJECTED)}
              className={`${
                isBitramp() ? "Decline_button" : "Outline_red_button"
              } `}
              disabled={isDisabled}
            >
              {formData?.status?.status === Status.REJECTED
                ? "Declined"
                : "Decline"}
              {formData?.status?.status === Status.REJECTED ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 ml-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : null}
            </button>
          </>
        ) : (
          <button type="submit" className="Primary_button">
            {addQuestionary.isPending ? "Loading..." : "Save"}
          </button>
        )}

        {canEdit ? (
          <Button
            onClick={() => {
              const payload = {
                ...formData,
                statement_of_purpose: getStatementOfPurposeRequestData(
                  sopData,
                  formData,
                  SoPData,
                ),
              };
              editQuestionaryMutation.mutate(payload);
            }}
            type="button"
            className="Primary_button"
          >
            Save
          </Button>
        ) : null}
        {error.message ? (
          <div className="text-red-500">{error.message}</div>
        ) : null}
      </div>
    </form>
  );
};

export default QuestionaryForm;
